<template>
  <div style="max-width: 75vw">
    <div>
      <div class="mb-8">
        <h2 class="page-title mb-2">Hipótese diagnóstica</h2>
        <p class="description-text">{{ diagnosticHypothesis }}</p>
      </div>
    </div>

    <div>
      <span class="font-weight-medium font-size-16 title-text">Altura e peso do paciente</span>
      <div class="d-flex mt-2">
        <v-text-field
          label="Altura (cm)"
          v-mask="['###']"
          v-model="patientHeight"
          @blur="updatePatientHeight"
          filled
          class="mr-6"
          clearable
        />
        <v-text-field
          label="Peso (kg)"
          v-model="patientWeight"
          @blur="updatePatientWeight"
          filled
          clearable
        />
      </div>
    </div>

    <div>
      <div>
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="page-title">Medicamentos</h2>
          <v-btn-toggle v-model="showAllTreatments" rounded dense>
            <v-btn
              :value="false"
              depressed
              height="28"
              :class="['text-none', { 'bg-active': !showAllTreatments, 'bg-inactive': showAllTreatments }]"
            >
              <v-icon size="18" class="pa-2" v-if="!showAllTreatments">mdi-check</v-icon>
              Ver indicados
            </v-btn>
            <v-btn
              :value="true"
              depressed
              height="28"
              :class="['text-none', { 'bg-active': showAllTreatments, 'bg-inactive': !showAllTreatments }]"
            >
              <v-icon size="18" class="pa-2" v-if="showAllTreatments">mdi-check</v-icon>
              Ver todos
            </v-btn>
          </v-btn-toggle>
        </div>
        <div v-click-outside="onBlurFilterTreatmentList">
          <v-text-field
            label="Buscar medicamento"
            outlined
            append-icon="mdi-magnify"
            clearable
            @click="handleSearchClick"
            @input="filterTreatmentList"
            v-model="treatmentSought"
          ></v-text-field>

          <v-expansion-panels v-model="panels" multiple>
            <v-expansion-panel
              class="mb-4"
              v-for="(treatment, idx) in treatmentList"
              :key="treatment.id"
              style="width: 50px"
            >
              <v-expansion-panel-header class="header rounded" title="Receitar">
                <template v-slot:actions>
                  <span class="text-subtitle-2" style="color: #1976d2">
                    {{ panels.includes(idx) ? 'Ocultar descrição' : 'Ver descrição' }}</span
                  >
                </template>
                <span class="text-subtitle-1">
                  <v-btn icon @click.stop="addSelectedTreatment(treatment)" class="mr-3" color="primary">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                  {{ treatment.medicine.name }}
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="content">
                {{ treatment.medicine.description }}
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>
    </div>

    <div>
      <h2 class="page-title">Novos medicamentos receitados</h2>

      <v-expansion-panels multiple>
        <v-expansion-panel class="mb-4" v-for="(treatment, idx) in selectedTreatments" :key="treatment.id">
          <v-expansion-panel-header class="header rounded" title="Retirar da receita" hide-actions>
            <span class="text-subtitle-1">
              <v-btn icon @click="selectedTreatments.splice(idx, 1)" class="mr-3" color="primary">
                <v-icon>mdi-close</v-icon>
              </v-btn>
              {{ treatment.medicine.name }}
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pl-2">
            <div class="d-flex flex-column">
              <v-autocomplete
                label="Selecionar prescrição"
                outlined
                hide-details
                menu-props="closeOnContentClick"
                returnObject
                :items="getTreatmentDosagesOptions(treatment)"
                v-model="treatment.selectedDosage"
                item-text="label"
              ></v-autocomplete>
              <p class="selected-treatment-info-title mt-2">
                {{ treatment.selectedDosage.text }}
              </p>
            </div>

            <div v-if="treatment.selectedDosage" class="mb-4">
              <div class="selected-treatment-info-title">Posologia:</div>
              <span
                class="text-pre-line selected-treatment-info-text"
                v-if="dosageDisplay[treatment.id] || treatment.selectedDosage.value?.length <= textLimit"
              >
                {{ treatment.selectedDosage.value }}
              </span>
              <span v-else class="text-pre-line selected-treatment-info-text">
                {{ treatment.selectedDosage.value?.slice(0, textLimit) }}...
              </span>
              <a
                v-if="treatment.selectedDosage.value?.length >= textLimit"
                @click="toggleDosage(treatment.id)"
                class="text-decoration-underline ml-1"
              >
                {{ dosageDisplay[treatment.id] ? 'Esconder' : 'Ver mais' }}
              </a>
            </div>

            <div v-if="treatment.guidelines" class="mb-4">
              <div class="selected-treatment-info-title">Orientações:</div>
              <span
                class="text-pre-line selected-treatment-info-text"
                v-if="guidelineDisplay[treatment.id] || treatment.guidelines?.length <= textLimit"
              >
                {{ treatment.guidelines }}
              </span>
              <span v-else class="text-pre-line selected-treatment-info-text"
              >{{ treatment.guidelines?.slice(0, textLimit) }}...
              </span>
              <a
                v-if="treatment.guidelines?.length >= textLimit"
                @click="toggleGuideline(treatment.id)"
                class="text-decoration-underline ml-1"
              >
                {{ guidelineDisplay[treatment.id] ? 'Esconder' : 'Ver mais' }}
              </a>
            </div>

            <div>
              <div class="selected-treatment-info-title">Informações:</div>
              <span
                class="text-pre-line selected-treatment-info-text"
                v-if="informationDisplay[treatment.id] || treatment.medicine.description?.length <= textLimit"
              >
                {{ treatment.medicine.description }}
              </span>
              <span v-else class="text-pre-line selected-treatment-info-text"
              >{{ treatment.medicine.description?.slice(0, textLimit) }}...
              </span>
              <a
                v-if="treatment.medicine.description?.length >= textLimit"
                @click="toggleInformation(treatment.id)"
                class="text-decoration-underline ml-1"
              >
                {{ informationDisplay[treatment.id] ? 'Esconder' : 'Ver mais' }}
              </a>
            </div>

            <div class="d-flex ml-auto mt-4 gap-2" :class="{ 'mr-auto mt-2': $vuetify.breakpoint.smAndDown }">
              <download-frame
                :url="getPrintMedicinePrescriptionUrl(treatment)"
                remoteUrl
                :onBeforeDownload="saveAllAnswers"
                :disabled="!treatment.selectedDosage?.id"
              >
                <template #default="{ loading }">
                  <v-btn
                    class="text-none"
                    text
                    color="primary"
                    :disabled="!treatment.selectedDosage?.id"
                    :loading="loading"
                  >
                    <v-icon>mdi-clipboard-edit-outline</v-icon>
                    Imprimir Receita
                  </v-btn>
                </template>
              </download-frame>

              <v-btn
                text
                class="text-none"
                color="primary"
                @click="openPrintExamPrescriptionModal(treatment)"
                :disabled="treatment.prescriptions.length === 0"
              >
                <v-icon>mdi-flask-empty-outline</v-icon>
                Imprimir prescrição de exames
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
    <v-dialog v-model="examModal" v-if="!!currentTreatment" max-width="600px">
      <v-card class="pb-4">
        <v-card-title class="justify-center"
        >Impressão de exames para {{ currentTreatment.medicine.name }}
        </v-card-title>

        <field-label
          v-if="examPrescriptionTemplates.length > 0"
          label="Modelo de impressão dos exames"
          titleClass="text-body-1"
          titleColorClass="black--text text--lighten-1"
          class="px-8"
        >
          <select-field
            :items="examPrescriptionTemplates"
            v-model="selectedExamPrescriptionTemplateId"
            itemValue="id"
            itemText="name"
            :disabled="examPrescriptionTemplates.length === 1"
          />
        </field-label>

        <div
          v-for="exam in currentTreatmentExams"
          :key="exam.id"
          class="d-flex align-center justify-space-between mx-12"
        >
          <span class="black--text text-body-1">{{ exam.name }}</span>

          <download-frame :url="printExamPrescriptionUrl(exam.id)" remoteUrl :onBeforeDownload="saveAllAnswers">
            <template #default="{ loading }">
              <v-btn icon large color="primary" :loading="loading">
                <v-icon>mdi-printer</v-icon>
              </v-btn>
            </template>
          </download-frame>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import DownloadFrame from '@/components/DownloadFrame.vue'
import { AGE_BREAKPOINTS } from '@/components/AttendanceForm/general/constants/age'
import { TREATMENT_EXHIBITION_RULES } from '@/store/attendance/config/treatmentExhibitionConfig'
import FieldLabel from '@/components/FieldLabel.vue'
import SelectField from '@/components/SelectField.vue'
import { mask } from 'vue-the-mask'
import { routes as api } from '@/plugins/api'
import store from '@/store'

const {
  mapMutations: newAttendanceFormMutations,
  mapGetters: newAttendanceFormGetters,
  mapActions: newAttendanceFormActions,
} = createNamespacedHelpers('newAttendanceForm')

export default {
  directives: {
    'click-outside': {
      bind: function(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          if (!(el == event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event)
          }
        }
        document.body.addEventListener('click', el.clickOutsideEvent)
      },
      unbind: function(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent)
      },
    },
    mask: {
      bind(el, binding, vnode, oldVnode) {
        if (binding.value) {
          mask(el, binding, vnode, oldVnode)
        }
      },
    },
  },
  components: {
    DownloadFrame,
    FieldLabel,
    SelectField,
  },
  data() {
    return {
      showAllTreatments: false,
      filteredTreatments: [],
      selectedTreatments: [],
      panels: [],
      active: true,
      dosageDisplay: {},
      guidelineDisplay: {},
      informationDisplay: {},
      currentTreatment: null,
      examModal: false,
      templates: [],
      selectedExamPrescriptionTemplateId: null,
      treatmentSought: null,
      searchFocus: false,
      patientHeight: null,
      patientWeight: null,
    }
  },
  computed: {
    ...newAttendanceFormGetters(['attendance', 'treatments', 'parameters', 'answers', 'questions', 'patientData']),
    treatmentList() {
      let result = []
      if (!this.showAllTreatments) {
        result = (this.filteredTreatments || []).filter((t) => {
          const exclusion = t.exhibitionConfig?.exclusion || []
          const inclusion = t.exhibitionConfig?.inclusion || []

          return (
            !exclusion.some((configKey) =>
              TREATMENT_EXHIBITION_RULES[t.disease.id].exclusion[configKey].check(this.attendance),
            ) &&
            inclusion.some((configKey) =>
              TREATMENT_EXHIBITION_RULES[t.disease.id].inclusion[configKey].check(this.attendance),
            )
          )
        })
      } else {
        result = (this.filteredTreatments || []).sort((a, b) => a.medicine.name.localeCompare(b.medicine.name))
      }
      return result.filter((treatment) => !this.selectedTreatments.some((item) => item.id === treatment.id))
    },
    textLimit() {
      return 165
    },
    isPatientAdult() {
      return this.$util.toAge(this.attendance.patient.birthday) >= AGE_BREAKPOINTS.child
    },

    diagnosticHypothesis() {
      if (this.answers['1'] !== 'sim') {
        return ''
      }

      const bsa =
        this.parameters['atopic-dermatitis:easi-bsa']?.score?.bsa &&
        this.parameters['atopic-dermatitis:easi-bsa'].score.bsa >= 10
      const easi =
        this.parameters['atopic-dermatitis:easi-bsa']?.score?.easi &&
        this.parameters['atopic-dermatitis:easi-bsa'].score.easi > 7
      const iga = this.parameters['atopic-dermatitis:iga']?.score && this.parameters['atopic-dermatitis:iga'].score >= 3
      const dlqi =
        this.parameters['dlqi-parameter']?.score &&
        (this.isPatientAdult
          ? this.parameters['dlqi-parameter'].score > 10
          : this.parameters['dlqi-parameter'].score > 12)
      const scorad =
        this.parameters['atopic-dermatitis:scorad']?.score && this.parameters['atopic-dermatitis:scorad'].score >= 25

      const check = scorad || easi || bsa || iga || dlqi
      return check ? 'Paciente com Dermatite Atópica moderada a grave.' : 'Paciente com Dermatite Atópica.'
    },

    currentTreatmentExams() {
      return this.currentTreatment?.prescriptions
        ?.filter((p) => !this.currentTreatment.selectedDosage || p[this.currentTreatment.selectedDosage.key])
        .map((p) => ({ ...p, name: p.exams.map((e) => e.name).join(', ') }))
    },
    examPrescriptionTemplates() {
      return this.templates.map((t) => ({ id: t.id, name: t.name }))
    },
  },
  watch: {
    selectedTreatments(value) {
      if (!value) {
        return
      }

      this.updateExtraAnswer({ name: 'selectedTreatments', value })
    },
  },
  methods: {
    ...newAttendanceFormMutations(['updateExtraAnswer', 'updatePatientData']),
    ...newAttendanceFormActions(['saveAllAnswers']),
    addSelectedTreatment(treatment) {
      this.selectedTreatments.push({
        ...treatment,
        selectedDosage: {},
      })
    },
    filterTreatmentList(event) {
      if (!(event?.length >= 3)) {
        this.filteredTreatments = this.treatments
        return
      }
      this.filteredTreatments = this.treatments.filter((t) =>
        t.medicine.name.toLowerCase().includes(event.toLowerCase()),
      )
    },
    handleSearchClick() {
      this.showAllTreatments = true
      this.searchFocus = true
    },
    onBlurFilterTreatmentList(event) {
      if (!this.searchFocus) {
        return
      }
      if (!this.treatmentSought) {
        this.showAllTreatments = false
        this.searchFocus = false
      }
    },
    getPrintMedicinePrescriptionUrl(treatment) {
      if (this.$util.apiURL()[this.$util.apiURL().length - 1] !== '/') {
        return `${this.$util.apiURL()}/treatment/dosage/print/${treatment.selectedDosage?.id}/${this.attendance.id}/${
          treatment.selectedDosage?.key === 'firstPrescription'
        }/${this.templates[0]?.id}`
      } else {
        return `${this.$util.apiURL()}treatment/dosage/print/${treatment.selectedDosage?.id}/${this.attendance.id}/${
          treatment.selectedDosage?.key === 'firstPrescription'
        }/${this.templates[0]?.id}`
      }
    },

    printExamPrescriptionUrl(prescriptionId) {
      if (this.$util.apiURL()[this.$util.apiURL().length - 1] !== '/') {
        return `${this.$util.apiURL()}/treatment/exam/print/${prescriptionId}/${this.attendance.id}/${
          this.selectedExamPrescriptionTemplateId
        }`
      } else {
        return `${this.$util.apiURL()}treatment/exam/print/${prescriptionId}/${this.attendance.id}/${
          this.selectedExamPrescriptionTemplateId
        }`
      }
    },

    getRangeText(min, max, unit) {
      if (!min && !max) {
        return `sem restrições de ${unit === 'anos' ? 'idade' : 'peso'}`
      }
      if (!min) {
        return `até ${max} ${unit}`
      }
      if (!max) {
        return `a partir de ${min} ${unit}`
      }

      return `de ${min} a ${max} ${unit}`
    },

    getTreatmentDosagesOptions(treatment) {
      let result = []
      let dosages = this.$util.copy(treatment.dosages)

      dosages = treatment.dosages.filter((d) => {
        const patientAge = this.$util.toAgeNumber(this.attendance.patient?.birthday)
        const patientWeight = this.attendance.patient?.currentWeightKg

        return (
          (d.minAge || 0) <= patientAge &&
          (d.maxAge || Infinity) >= patientAge &&
          (d.minWeight || 0) <= patientWeight &&
          (d.maxWeight || Infinity) >= patientWeight
        )
      })

      result.push(
        ...dosages.map((d) => ({
          key: 'firstPrescription',
          label: 'Primeira prescrição',
          text: `Primeira prescrição | ${this.getRangeText(d.minAge, d.maxAge, 'anos')} | ${this.getRangeText(
            d.minWeight,
            d.maxWeight,
            'kg',
          )}`,
          value: d.firstPrescription,
          id: d.id,
          lme: d.treatmentDosageLme,
        })),
      )

      result.push(
        ...dosages.map((d) => ({
          key: 'maintenancePrescription',
          label: 'Manutenção',
          text: `Manutenção | ${this.getRangeText(d.minAge, d.maxAge, 'anos')} | ${this.getRangeText(
            d.minWeight,
            d.maxWeight,
            'kg',
          )}`,
          value: d.maintenancePrescription,
          id: d.id,
          lme: d.treatmentDosageLme,
        })),
      )
      return result
    },
    toggleDosage(id) {
      this.$set(this.dosageDisplay, id, !this.dosageDisplay[id])
    },
    toggleGuideline(id) {
      this.$set(this.guidelineDisplay, id, !this.guidelineDisplay[id])
    },
    toggleInformation(id) {
      this.$set(this.informationDisplay, id, !this.informationDisplay[id])
    },
    openPrintExamPrescriptionModal(treatment) {
      this.currentTreatment = treatment
      this.examModal = true
    },
    async updatePatientHeight() {
      this.updatePatientData({ height: this.patientHeight })
      this.attendance.heightCm = this.patientHeight
      await this.$api.partialUpdatePatient({ id: this.patientData.id, currentHeightCm: Number(this.patientHeight) })
      await this.$api.patchAttendance(this.attendance.id, this.attendance)
    },
    async updatePatientWeight() {
      this.updatePatientData({ weight: this.patientWeight })
      this.attendance.weightKg = this.patientWeight
      await this.$api.partialUpdatePatient({ id: this.patientData.id, currentWeightKg: Number(this.patientWeight) })
      await this.$api.patchAttendance(this.attendance.id, this.attendance)
    },
  },

  async mounted() {
    this.filteredTreatments = this.treatments
    this.selectedTreatments = this.$store.getters['newAttendanceForm/extraAnswers']?.selectedTreatments || []

    this.templates = (await this.$api.getTemplates()).items
    this.selectedExamPrescriptionTemplateId = this.examPrescriptionTemplates[0]?.id
  },
}
</script>

<style scoped>
.page-title {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: var(--v-neutral-lighten17);
}

.description-text {
  color: var(--v-neutral-lighten17);
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.header {
  height: 64px;
}

.title-text {
  color: #2b2b2b;
}

.content {
  color: #808080;
  line-height: 20px;
  margin-left: 36px;
}

.selected-treatment-info-title {
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: var(--v-neutral-lighten17);
}

.selected-treatment-info-text {
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: var(--v-neutral-lighten17);
}

.bg-active {
  background-color: #e0f0ff !important;
}

.bg-inactive {
  background-color: #ffffff !important;
  opacity: 1 !important;
}

.v-btn.v-item--active::before {
  background-color: #ffffff;
}
</style>
